import * as React from 'react'

import { isPast } from 'date-fns'

import { useIsSplitEnabled } from '@buffer-mono/features'
import { useDelayedLoading, VisuallyHidden } from '@buffer-mono/popcorn'

import { ContentItemCard } from '~publish/components/ContentItemCard'
import { PostCard, PostCard_Post } from '~publish/components/PostCard'
import type { PostCard_PostData } from '~publish/components/PostCard'
import { useChannelAgnosticDraftsEnabled } from '~publish/hooks/useChannelAgnosticDraftsEnabled'
import { getRecurrenceSummary } from '~publish/components/PostCard/PostCardActions/recurrenceHelpers'
import { PostingTimeline } from '~publish/components/PostingTimeline'
import type { PostTab } from '~publish/components/PostTabs'
import { maskFragments, readFragment } from '~publish/graphql'
import {
  isPausedPost,
  isScheduledPostOrDraft,
  isUnscheduledApproval,
  isUnscheduledDraft,
  postStatusByTab,
} from '~publish/helpers/post'
import type { FilterablePostType } from '~publish/helpers/postType'
import { useTimezone } from '~publish/hooks/useTimezone'

import { buildScheduledEntries } from './buildScheduledEntries'
import { DraftsList } from './DraftsList'
import { getPaginationSentinelIds } from './getPaginationSentinelIds'
import { PostListError } from './PostListError'
import {
  FilteredPostListEmptyState,
  PostListEmptyState,
  PostListSkeleton,
} from './PostListStates'
import { usePaginatedPostList } from './usePaginatedPostList'
import { DateSeparator } from '../../../components/DateSeparator'

import type { JSX } from 'react'

export { FilteredPostListEmptyState, PostListEmptyState, PostListSkeleton }

type PostListProps = {
  status: PostTab
  /** Omit for no channel restriction. An empty array is a deliberate "no real channels" selection — distinct from omitting it entirely — and only makes sense combined with `includeChannellessDrafts`. */
  channelIds?: string[]
  tagIds?: string[]
  postTypes?: readonly FilterablePostType[]
  emptyState?: React.ReactNode
  showChannelNotices?: boolean
  /** Folds channel-less content item drafts into the drafts tab, alongside any `channelIds` match. */
  includeChannellessDrafts?: boolean
  sectionHeading?: string
}

/**
 * Routes the drafts tab to the unified `drafts` query when channel-agnostic
 * drafts are enabled. `includeChannellessDrafts` wins outright over any
 * post-type filter: content items have no post type, so a "No channels"
 * selection always shows channel-less drafts, matching how a dedicated
 * channel-less view worked before this was unified — a post-type filter
 * combined with it (unreachable through the UI, but not through a stale URL)
 * would otherwise silently widen to every channel's drafts by falling
 * through to the posts-only list below. A tag filter, unlike post type,
 * never leaves the unified query: content items carry tags too, so it filters
 * both kinds by it instead of dropping it. Otherwise, the unified query only
 * applies when no post-type filter narrows the tab and no specific channel
 * was picked — `channelIds` omitted, not merely empty,
 * since an empty array is a deliberate "no real channels" selection that
 * must still narrow the result (@see PostListProps.channelIds). Every other
 * case renders the posts-only list unchanged.
 */
export const PostList = (props: PostListProps): JSX.Element => {
  const channelAgnosticDraftsEnabled = useChannelAgnosticDraftsEnabled()
  const showUnifiedDrafts =
    channelAgnosticDraftsEnabled &&
    props.status === 'drafts' &&
    (props.includeChannellessDrafts ||
      (!props.postTypes?.length && props.channelIds === undefined))

  if (showUnifiedDrafts) {
    return (
      <DraftsList
        emptyState={props.emptyState}
        showChannelNotices={props.showChannelNotices}
        channelIds={props.channelIds}
        includeChannellessDrafts={props.includeChannellessDrafts}
        tagIds={props.tagIds}
        sectionHeading={props.sectionHeading}
      />
    )
  }

  return <PostsOnlyList {...props} />
}

const PostsOnlyList = ({
  status,
  channelIds,
  tagIds,
  postTypes,
  emptyState,
  showChannelNotices = false,
  sectionHeading,
}: PostListProps): JSX.Element => {
  const isRecurringEnabled = useIsSplitEnabled('publish-recurring-posts')
  const timeZone = useTimezone()
  const { data, loading, error, lastElementRef, fetchingMore } =
    usePaginatedPostList({
      status: postStatusByTab[status],
      channelIds,
      tagIds,
      postTypes,
      // For the 'sent' tab we want to see the most recently posted first
      sortPreset:
        status === 'sent' ? 'mostRecentlyPostedFirst' : 'upcomingFirst',
      noteCounts: 'notesButton',
    })

  const pendingFirstLoad =
    (loading && !data?.posts.edges?.length) ||
    (typeof data === 'undefined' && !error)
  // The skeleton reveals after the CSS delay. The hook holds it mounted for a
  // minimum duration, so a response just after the reveal never cuts the fade
  // short.
  const heldLoading = useDelayedLoading(pendingFirstLoad)
  const renderWithSectionHeading = (content: React.ReactNode): JSX.Element => (
    <>
      {sectionHeading && (
        <VisuallyHidden as="h2">{sectionHeading}</VisuallyHidden>
      )}
      {content}
    </>
  )

  if (pendingFirstLoad || heldLoading) {
    return renderWithSectionHeading(<PostListSkeleton />)
  }

  if (error) {
    return renderWithSectionHeading(<PostListError error={error} />)
  }

  if (!loading && !data?.posts.edges?.length) {
    return renderWithSectionHeading(
      emptyState ? emptyState : <PostListEmptyState />,
    )
  }

  const flattenedPostEdges = data?.posts.edges?.map((edge) => edge.node) ?? []
  const posts = readFragment(PostCard_Post, flattenedPostEdges)

  const pausedPosts = posts.filter(isPausedPost)
  const unscheduledDrafts = posts.filter(isUnscheduledDraft)
  const unscheduledApprovals = posts.filter(isUnscheduledApproval)

  const scheduledPosts = posts.filter(
    (post): post is PostCard_PostData & { dueAt: string } =>
      isScheduledPostOrDraft(post) &&
      postStatusByTab[status].includes(post.status),
  )

  const scheduledEntries = buildScheduledEntries(scheduledPosts, [])

  const { lastPostId } = getPaginationSentinelIds({
    posts,
    scheduledEntries,
    datedContentItems: [],
    undatedContentItems: [],
  })

  return renderWithSectionHeading(
    <PostingTimeline
      role="feed"
      aria-busy={fetchingMore}
      data-testid="post-list"
    >
      {pausedPosts.length > 0 && (
        <>
          <PostingTimeline.Header key="unpublished-posts-header">
            Not Published
          </PostingTimeline.Header>
          {pausedPosts.map((post) => {
            const summary =
              post.status === 'scheduled'
                ? getRecurrenceSummary(
                    post.recurrence,
                    post.dueAt,
                    isRecurringEnabled,
                    timeZone,
                  )
                : null
            return (
              <PostingTimeline.Entry key={post.id}>
                <PostingTimeline.TimeLabel
                  date={post.dueAt}
                  status={post.status}
                  notificationStatus={post.notificationStatus}
                  schedulingType={post.schedulingType}
                  overdue={isPast(post.dueAt) && post.error === null}
                  customScheduled={post.isCustomScheduled}
                  isPinned={post.isPinned}
                  includeDate
                  recurring={!!summary}
                  recurrenceSummary={summary}
                />
                <PostingTimeline.Content>
                  <PostCard
                    post={maskFragments([PostCard_Post], post)}
                    ref={post.id === lastPostId ? lastElementRef : null}
                    showChannelNotices={showChannelNotices}
                    rounded="lg"
                  />
                </PostingTimeline.Content>
              </PostingTimeline.Entry>
            )
          })}
        </>
      )}
      {status === 'drafts' && unscheduledDrafts.length > 0 && (
        <>
          <PostingTimeline.Header key="unscheduled-drafts-header">
            Unscheduled
          </PostingTimeline.Header>
          {unscheduledDrafts.map((post) => (
            <PostingTimeline.Entry key={post.id}>
              <PostingTimeline.TimeLabel
                status={post.status}
                notificationStatus={post.notificationStatus}
                schedulingType={post.schedulingType}
              />
              <PostingTimeline.Content>
                <PostCard
                  key={post.id}
                  post={maskFragments([PostCard_Post], post)}
                  ref={post.id === lastPostId ? lastElementRef : null}
                  showChannelNotices={showChannelNotices}
                  rounded="lg"
                />
              </PostingTimeline.Content>
            </PostingTimeline.Entry>
          ))}
        </>
      )}
      {unscheduledApprovals.length > 0 && status === 'approvals' && (
        <>
          <PostingTimeline.Header key="unscheduled-approvals-header">
            Unscheduled
          </PostingTimeline.Header>
          {unscheduledApprovals.map((post) => (
            <PostingTimeline.Entry key={`${post.id}-${post.status}`}>
              <PostingTimeline.TimeLabel
                status={post.status}
                notificationStatus={post.notificationStatus}
                schedulingType={post.schedulingType}
              />
              <PostingTimeline.Content>
                <PostCard
                  key={`${post.id}-${post.status}`}
                  post={maskFragments([PostCard_Post], post)}
                  ref={post.id === lastPostId ? lastElementRef : null}
                  showChannelNotices={showChannelNotices}
                  rounded="lg"
                />
              </PostingTimeline.Content>
            </PostingTimeline.Entry>
          ))}
        </>
      )}
      {scheduledEntries.map((entry, index, list) => {
        const currentDate = entry.date
        const prevDate = list[index - 1]?.date

        if (entry.kind === 'contentItem') {
          const { item } = entry
          return (
            <React.Fragment key={`date-header-ci-${item.id}`}>
              <DateSeparator
                currentDate={currentDate}
                previousDate={prevDate}
              />
              <PostingTimeline.Entry key={item.id}>
                <PostingTimeline.TimeLabel
                  status="draft"
                  date={item.targetDate}
                />
                <PostingTimeline.Content>
                  <ContentItemCard contentItem={item} />
                </PostingTimeline.Content>
              </PostingTimeline.Entry>
            </React.Fragment>
          )
        }

        const { post } = entry
        const isDraft =
          post.status === 'draft' || post.status === 'needs_approval'
        const hasScheduledAt = post.isCustomScheduled || post.isPinned
        const isTentative = hasScheduledAt && isDraft
        const summary =
          post.status === 'scheduled'
            ? getRecurrenceSummary(
                post.recurrence,
                post.dueAt,
                isRecurringEnabled,
                timeZone,
              )
            : null

        return (
          <React.Fragment key={`date-header-${post.id}`}>
            {/* TODO: refactor date header to be part of the PostingTimeline */}
            <DateSeparator currentDate={currentDate} previousDate={prevDate} />
            <PostingTimeline.Entry key={`${post.id}-${post.status}`}>
              <PostingTimeline.TimeLabel
                date={currentDate}
                status={post.status}
                notificationStatus={post.notificationStatus}
                schedulingType={post.schedulingType}
                tentative={isTentative}
                overdue={
                  isPast(post.dueAt) &&
                  post.status !== 'sent' &&
                  post.error === null
                }
                customScheduled={post.isCustomScheduled}
                isPinned={post.isPinned}
                recurring={!!summary}
                recurrenceSummary={summary}
              />
              <PostingTimeline.Content>
                <PostCard
                  key={`${post.id}-${post.status}`}
                  post={maskFragments([PostCard_Post], post)}
                  ref={post.id === lastPostId ? lastElementRef : null}
                  showChannelNotices={showChannelNotices}
                  rounded="lg"
                />
              </PostingTimeline.Content>
            </PostingTimeline.Entry>
          </React.Fragment>
        )
      })}

      {data?.posts.pageInfo.hasNextPage && (
        <PostingTimeline.Loading key="load-more" />
      )}
    </PostingTimeline>,
  )
}
